// Small shared helpers: a promise mutex and a status store.

/** Runs async sections one at a time, in call order. */
export class Mutex {
  #tail: Promise<unknown> = Promise.resolve();

  run<T>(fn: () => Promise<T>): Promise<T> {
    const result = this.#tail.then(fn, fn);
    // Keep the chain alive whatever `fn` does.
    this.#tail = result.catch(() => undefined);
    return result;
  }
}

/**
 * A value with listeners; `set` notifies only when something changed (shallow).
 *
 * Every `subscribe` is its own subscription, even with a listener already subscribed: its
 * unsubscribe ends only that one. (A set of listener functions would let one caller's
 * unsubscribe silently end another's with the same function, e.g. a module-level callback
 * shared by several React components: the station's sync pill froze that way.)
 */
export class Store<T extends object> {
  #value: T;
  readonly #listeners = new Set<{ readonly listener: (value: T) => void }>();

  constructor(initial: T) {
    this.#value = initial;
  }

  get(): T {
    return this.#value;
  }

  set(patch: Partial<T>): void {
    const next = { ...this.#value, ...patch };
    const changed = (Object.keys(patch) as (keyof T)[]).some(
      (k) => !sameValue(next[k], this.#value[k]),
    );
    if (!changed) return;
    this.#value = next;
    for (const s of [...this.#listeners]) s.listener(next);
  }

  subscribe(listener: (value: T) => void): () => void {
    const subscription = { listener };
    this.#listeners.add(subscription);
    return () => {
      this.#listeners.delete(subscription);
    };
  }
}

function sameValue(a: unknown, b: unknown): boolean {
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (a !== null && b !== null && typeof a === 'object' && typeof b === 'object') {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return Object.is(a, b);
}

/** The message of anything thrown. */
export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
